import React from 'react';
import { humanizeKey } from '../../utils/humanizeKey';
import { Mail, Phone, User } from 'lucide-react';
import { hasMultipleOptions, getComponentOptions, getOptionTotal } from '../Quotes/optionUtils';

// Voyager Pro reskin - 'modern' is the only template with NO per-template
// palette override in Figma (QUOTE_TEMPLATES[].vars is {} for 'modern') -
// it IS the base Voyager Pro theme already used everywhere else in this
// reskin, so this file just adopts the same var(--x) tokens/typography as
// every other reskinned surface (see styles/voyagerTheme.css), rather than
// getting its own palette file entry.

const ModernTemplate = ({ quote }) => {
  if (!quote) {
    return null;
  }

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const [year, month, day] = dateString.split('-');
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="voyager-app min-h-screen bg-[var(--background)]">
      {/* Header Section */}
      <div className="bg-white shadow-sm border-b border-[var(--border)]">
        <div className="max-w-5xl mx-auto px-6 py-8">
          <h1 style={{ fontFamily: 'var(--font-display)' }} className="text-3xl text-[var(--foreground)] mb-2">
            {quote.quoteTitle || 'Travel Quote'}
          </h1>
          <div className="flex flex-wrap gap-4 text-sm text-[var(--muted-foreground)]">
            {(quote.client?.firstName || quote.client?.lastName) && (
              <div className="flex items-center gap-2">
                <span className="font-medium text-[var(--foreground)]">Client:</span>
                <span>{quote.client.firstName} {quote.client.lastName}</span>
              </div>
            )}
            {quote.travelDates?.start && (
              <div className="flex items-center gap-2">
                <span className="font-medium text-[var(--foreground)]">Travel Dates:</span>
                <span>
                  {formatDate(quote.travelDates.start)}
                  {quote.travelDates.end && ` - ${formatDate(quote.travelDates.end)}`}
                </span>
              </div>
            )}
            {(quote.travelers?.adults || quote.travelers?.children?.length) && (
              <div className="flex items-center gap-2">
                <span className="font-medium text-[var(--foreground)]">Travelers:</span>
                <span>
                  {quote.travelers.adults || 0} Adult{quote.travelers.adults !== 1 ? 's' : ''}
                  {quote.travelers.children?.length > 0 && `, ${quote.travelers.children.length} Child${quote.travelers.children.length !== 1 ? 'ren' : ''}`}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Components Section */}
      <div className="max-w-5xl mx-auto px-6 py-8">
        <h2 style={{ fontFamily: 'var(--font-display)' }} className="text-2xl text-[var(--foreground)] mb-6">Trip Components</h2>

        {quote.components && quote.components.length > 0 ? (
          <div className="space-y-4">
            {quote.components.map((component, index) => (
              <div
                key={component.id || index}
                className="bg-white rounded-2xl shadow-sm border border-[var(--border)] p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex justify-between items-start mb-3">
                  <h3 className="text-xl font-semibold text-[var(--foreground)]">
                    {component.title || `Component ${index + 1}`}
                  </h3>
                  <span className="text-lg font-bold" style={{ color: 'var(--accent)' }}>
                    {formatCurrency(component.price)}
                  </span>
                </div>

                <div className="text-sm text-[var(--muted-foreground)] mb-2">
                  <span className="inline-block bg-[var(--muted)] px-3 py-1 rounded-full">
                    {component.type || 'General'}
                  </span>
                </div>

                {component.data && Object.keys(component.data).length > 0 && (
                  <div className="mt-4 pt-4 border-t border-[var(--border)]">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                      {Object.entries(component.data).map(([key, value]) => {
                        if (!value || key === 'isOption' || key === 'optionDescription') return null;

                        const isPriceField = key.toLowerCase().includes('price') ||
                                             key.toLowerCase().includes('cost') ||
                                             key.toLowerCase().includes('fee') ||
                                             key.toLowerCase().includes('total') ||
                                             key.toLowerCase().includes('deposit');
                        const isNumeric = !isNaN(parseFloat(value)) && isFinite(value);
                        const displayValue = isPriceField && isNumeric ? formatCurrency(parseFloat(value)) : String(value);

                        return (
                          <div key={key} className="flex flex-col">
                            <span className="font-medium text-[var(--foreground)] capitalize">
                              {humanizeKey(key)}:
                            </span>
                            <span className="text-[var(--muted-foreground)]">{displayValue}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Component Photos */}
                {component.photos && component.photos.length > 0 && (
                  <div className="mt-6 pt-6 border-t border-[var(--border)]">
                    <h4 className="text-sm font-semibold text-[var(--foreground)] mb-3 flex items-center gap-2">
                      <span>📸</span> Photos
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {component.photos.map((photo, photoIndex) => (
                        <div
                          key={photoIndex}
                          className="relative group cursor-pointer rounded-xl overflow-hidden shadow hover:shadow-md transition-shadow border border-[var(--border)] bg-white"
                          onClick={() => window.open(photo.url, '_blank')}
                        >
                          <img
                            src={photo.url}
                            alt={photo.name || 'Component photo'}
                            className="w-full h-auto object-contain"
                            style={{ maxHeight: '300px' }}
                          />
                          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <span className="text-white text-sm font-medium">{photo.name || 'View Photo'}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Component Documents */}
                {component.documents && component.documents.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-sm font-semibold text-[var(--foreground)] mb-2 flex items-center gap-2">
                      <span>📋</span> Documents
                    </h4>
                    <div className="space-y-2">
                      {component.documents.map((doc, docIndex) => (
                        <a
                          key={docIndex}
                          href={doc.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 bg-[var(--muted)] hover:bg-[var(--border)] px-4 py-2 rounded-xl transition-colors border border-[var(--border)]"
                        >
                          <span>📄</span>
                          <span className="text-[var(--foreground)] text-sm font-medium">{doc.name}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* Alternative options for comparison - only rendered when
                    the agent has actually added more than one (see
                    optionUtils.js); a component with zero or one option
                    renders exactly as it always has, nothing below this
                    point is new for existing quotes. */}
                {hasMultipleOptions(component) && (
                  <div className="mt-6 pt-6 border-t border-[var(--border)]">
                    <h4 className="text-sm font-semibold text-[var(--foreground)] mb-3">Choose Your Option</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {getComponentOptions(component).map((option, optIndex) => (
                        <div key={option.id || optIndex} className="rounded-xl border-2 overflow-hidden"
                          style={{ borderColor: optIndex === 0 ? 'var(--accent)' : 'var(--border)' }}>
                          {option.photoUrl && (
                            <img src={option.photoUrl} alt={option.label || 'Option'} className="w-full h-32 object-cover" />
                          )}
                          <div className="p-4">
                            <div className="flex items-center justify-between gap-2 mb-1">
                              <span className="font-semibold text-sm text-[var(--foreground)]">{option.label || `Option ${optIndex + 1}`}</span>
                              {option.badge && (
                                <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: 'rgba(196,135,31,0.1)', color: 'var(--accent)' }}>{option.badge}</span>
                              )}
                            </div>
                            {option.description && (
                              <p className="text-xs text-[var(--muted-foreground)] mb-2">{option.description}</p>
                            )}
                            <p className="text-lg font-bold" style={{ color: 'var(--accent)' }}>{formatCurrency(getOptionTotal(option))}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm border border-[var(--border)] p-8 text-center text-[var(--muted-foreground)]">
            No components added yet
          </div>
        )}

        {/* Pricing Summary */}
        <div className="mt-8 bg-white rounded-2xl shadow-sm border border-[var(--border)] p-6">
          <h3 style={{ fontFamily: 'var(--font-display)' }} className="text-xl text-[var(--foreground)] mb-4">Pricing Summary</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center text-lg">
              <span className="font-medium text-[var(--muted-foreground)]">Total Amount:</span>
              <span className="font-bold text-[var(--foreground)]">
                {formatCurrency(quote.totalPrice)}
              </span>
            </div>
            {quote.depositAmount > 0 && (
              <div className="flex justify-between items-center pt-3 border-t border-[var(--border)]">
                <span className="font-medium text-[var(--muted-foreground)]">Deposit Due:</span>
                <span className="font-bold" style={{ color: 'var(--accent)' }}>
                  {formatCurrency(quote.depositAmount)}
                </span>
              </div>
            )}
            {quote.depositDueDate && (
              <div className="flex justify-between items-center text-sm">
                <span className="text-[var(--muted-foreground)]">Deposit Due Date:</span>
                <span className="text-[var(--foreground)]">{formatDate(quote.depositDueDate)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Notes Section */}
        {quote.notes && (
          <div className="mt-6 bg-[var(--muted)] rounded-2xl border border-[var(--border)] p-6">
            <h3 className="text-lg font-semibold text-[var(--foreground)] mb-2">Additional Notes</h3>
            <p className="text-[var(--foreground)] whitespace-pre-wrap">{quote.notes}</p>
          </div>
        )}

        {/* Agent Contact Footer */}
        {(quote.agent_name || quote.agent_email || quote.agent_phone) && (
          <div className="mt-8 bg-white rounded-2xl shadow-sm border border-[var(--border)] p-6">
            <div className="flex items-center justify-center gap-2 mb-4">
              <User className="w-5 h-5 text-[var(--muted-foreground)]" />
              <h3 className="text-lg font-semibold text-[var(--foreground)]">Your Travel Advisor</h3>
            </div>

            {quote.agent_name && (
              <div className="text-center mb-4">
                <p className="text-xl font-bold text-[var(--foreground)]">{quote.agent_name}</p>
                {quote.agent_business_name && (
                  <p className="text-[var(--muted-foreground)] font-medium">{quote.agent_business_name}</p>
                )}
              </div>
            )}

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              {quote.agent_email && (
                <a
                  href={`mailto:${quote.agent_email}`}
                  className="flex items-center gap-2 text-[var(--muted-foreground)] hover:text-[var(--accent)] transition-colors"
                >
                  <Mail className="w-4 h-4" />
                  <span className="text-sm font-medium">{quote.agent_email}</span>
                </a>
              )}

              {quote.agent_email && quote.agent_phone && (
                <span className="hidden sm:block text-[var(--border)]">|</span>
              )}

              {quote.agent_phone && (
                <a
                  href={`tel:${quote.agent_phone}`}
                  className="flex items-center gap-2 text-[var(--muted-foreground)] hover:text-[var(--accent)] transition-colors"
                >
                  <Phone className="w-4 h-4" />
                  <span className="text-sm font-medium">{quote.agent_phone}</span>
                </a>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ModernTemplate;
